import { ThunkDispatch } from "redux-thunk";

import { getBaseStore } from "@utils/configuration/aem";

import HooksService, { HookKeys } from "@services/hooks.services";
import * as WishlistService from "@services/localWishlist.service";
import {
    addProductToLocalWishlist,
    getLocalWishlist,
    isWishlistFull,
    removeProductFromLocalWishlist,
} from "@services/localWishlist.service";
import { ProductImpressionModel } from "@services/tracking";
import { trackAddToWishlistImpression } from "@services/tracking/trackers";

import CreateApiModule from "@redux/create-api-module";
import { AddToWishlistStatus } from "@redux/modules/api/data/wishlist/@types/addToWishlistStatus.enum";
import { broadcastWishlistError } from "@redux/modules/api/data/wishlist/wishlist.utils";
import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";
import { translateKey } from "@redux/modules/value/translations/translations.helpers";
import { getTranslations } from "@redux/modules/value/translations/translations.selector";
import { set as setWishlistModal } from "@redux/modules/value/wishlistModal/wishlistModal.module";
import { update } from "@redux/modules/value/wishlistSidebar/wishlistSidebar.module";
import { IState, TApiAction, TExtraThunkArgs } from "@redux/store.types";
import { getApiDataState } from "@redux/utils";

import { $CustomerModuleName } from "../customer/customer.types";
import { WishlistModel, WishlistProductModel } from "./@types/wishlist.model";
import { DISCRIMINATORS } from "./wishlist.discriminators";
import WishlistReducer from "./wishlist.reducer";
import { getFirstWishList } from "./wishlist.selectors";
import { $WishlistModuleName } from "./wishlist.types";

const { actions, constants } = CreateApiModule(
    $WishlistModuleName,
    ({ url }) => `/customerservice/${getBaseStore()}/customers/${url}`,
    {
        reducer: WishlistReducer,
    },
);

export const loadAll =
    (force: boolean = false) =>
    async (
        dispatch: ThunkDispatch<
            IState,
            TExtraThunkArgs,
            TApiAction<Array<WishlistModel>>
        >,
        getState: () => IState,
    ) => {
        const customer = getApiDataState(getState(), $CustomerModuleName)?.data;
        const isLoggedIn = !!(customer && !customer.guest);

        const translations = getTranslations(getState());
        const wishlistName = WishlistService.getNewName([], translations);

        if (!isLoggedIn) {
            const localList = getLocalWishlist();
            return dispatch({
                type: constants.SUCCESS,
                payload: [
                    {
                        name: wishlistName,
                        products: localList,
                    },
                ],
            } as TApiAction);
        }

        return await dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists`,
                    },
                },
                "GET",
                force,
                {
                    skipErrors: [404],
                },
            ),
        );
    };

export const createWishlist =
    (name: string, products: Array<WishlistProductModel> = []) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
    ): Promise<TApiAction<WishlistModel>> => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists`,
                    },
                    data: {
                        name,
                        products,
                    },
                },
                "POST",
                false,
                {
                    discriminator: DISCRIMINATORS.LoadOne,
                },
            ),
        );

        if (action.type === constants.FAILURE) {
            if (action.payload.message) {
                broadcastMessage({
                    id: "wishlist-error",
                    dismissible: true,
                    activeScroll: true,
                    status: "danger",
                    text: action.payload.message,
                });
            }
        }

        return action;
    };

export const addItemsToList =
    (
        wishlistId: string,
        skus: Array<string>,
        trackingProducts: Array<ProductImpressionModel>,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists/${wishlistId}/products/bulk`,
                    },
                    data: {
                        skuList: skus,
                    },
                },
                "POST",
                false,
                {
                    discriminator: DISCRIMINATORS.LoadOne,
                },
            ),
        );

        if (action.type === constants.SUCCESS) {
            trackingProducts.map(trackAddToWishlistImpression);
        }

        if (action.type === constants.FAILURE) {
            const message = action.payload.message;
            message && broadcastWishlistError(action.payload.message);
        }

        return action;
    };

export const addProductDirectly =
    (
        wishlistId: string,
        sku: string,
        trackingProduct: ProductImpressionModel,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists/${wishlistId}/products`,
                    },
                    data: {
                        sku,
                    },
                },
                "POST",
                false,
                {
                    discriminator: DISCRIMINATORS.LoadOne,
                },
            ),
        );

        if (action.type === constants.SUCCESS) {
            trackAddToWishlistImpression(trackingProduct);
        }

        if (action.type === constants.FAILURE) {
            const message = action.payload.message;
            message && broadcastWishlistError(action.payload.message);
        }

        return action;
    };

const addProductLocal =
    (sku: string, trackingProduct: ProductImpressionModel) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, any>,
        getState: () => IState,
    ): Promise<AddToWishlistStatus> => {
        const translations = getTranslations(getState());
        const wishlistName = WishlistService.getNewName([], translations);

        if (isWishlistFull()) {
            const errorMessage = translateKey(
                translations,
                "wishlist.error.maxCountExceeded",
            );

            broadcastWishlistError(errorMessage);

            dispatch({
                type: constants.FAILURE,
                payload: [
                    {
                        message: errorMessage,
                    },
                ],
            });

            return AddToWishlistStatus.FAILED;
        } else {
            dispatch({
                type: constants.SUCCESS,
                payload: [
                    {
                        name: wishlistName,
                        products: addProductToLocalWishlist(
                            sku,
                            trackingProduct,
                        ),
                    },
                ],
            });

            trackAddToWishlistImpression(trackingProduct);

            return AddToWishlistStatus.ADDED;
        }
    };

export const move =
    (sku: string, sourceListId: string, targetListId: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists/${sourceListId}/products/${sku}/move`,
                    },
                    data: {
                        targetListId,
                    },
                },
                "POST",
                false,
            ),
        );
    };

export const addProduct =
    (sku: string, trackingProduct: ProductImpressionModel) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, any>,
        getState: () => IState,
    ): Promise<AddToWishlistStatus> => {
        const customer = getApiDataState(getState(), $CustomerModuleName)?.data;
        const isLoggedIn = !!(customer && !customer.guest);

        if (!isLoggedIn) {
            if (HooksService.hasCallbacksFor(HookKeys.ADD_TO_WISHLIST)) {
                HooksService.triggerCallback(HookKeys.ADD_TO_WISHLIST);
                return AddToWishlistStatus.FAILED;
            }
            return await addProductLocal(sku, trackingProduct)(
                dispatch,
                getState,
            );
        }

        const translations = getTranslations(getState());
        const wishlistName = WishlistService.getNewName([], translations);
        const wishlists =
            getApiDataState(getState(), $WishlistModuleName)?.data || [];

        // If the user owns no wishlists, create one for them.
        if (wishlists.length === 0) {
            const action = await dispatch(
                createWishlist(wishlistName, [{ sku }]),
            );
            if (action.type === constants.SUCCESS) {
                trackAddToWishlistImpression(trackingProduct);
            }
            return action.type === constants.FAILURE
                ? AddToWishlistStatus.FAILED
                : AddToWishlistStatus.ADDED;
        }

        // There is only one wishlist, no need to show the wishlist modal
        if (wishlists.length === 1) {
            const action = await dispatch(
                addProductDirectly(wishlists[0].id, sku, trackingProduct),
            );
            return action.type === constants.FAILURE
                ? AddToWishlistStatus.FAILED
                : AddToWishlistStatus.ADDED;
        }

        // There are multiple wishlists, show modal
        dispatch(
            setWishlistModal({
                visible: true,
                product: {
                    ...trackingProduct.product,
                },
            }),
        );
        return AddToWishlistStatus.WAITING_FOR_USER_ACTION;
    };

export const selectFirstList =
    () =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, any>,
        getState: () => IState,
    ) => {
        const firstWishlist = getFirstWishList(getState());

        return dispatch(update({ currentListId: firstWishlist?.id }));
    };

export const removeProduct =
    (sku: string, withReload: boolean = false) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, any>,
        getState: () => IState,
    ) => {
        const customer = getApiDataState(getState(), $CustomerModuleName)?.data;
        const isLoggedIn = !!(customer && !customer.guest);

        if (!isLoggedIn) {
            return dispatch({
                type: constants.SUCCESS,
                payload: [
                    {
                        products: removeProductFromLocalWishlist(sku),
                    },
                ],
            });
        }

        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `wishlists/products/${sku}`,
                    },
                    data: {
                        sku,
                    },
                },
                "DELETE",
                false,
            ),
        );

        if (withReload) {
            if (action.type === constants.SUCCESS) {
                await dispatch(loadAll(true));
            }
        }

        return action;
    };

export const rename =
    (id: string, newName: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/wishlists/${id}`,
                    },
                    data: {
                        name: newName,
                    },
                },
                "PUT",
                false,
                {
                    discriminator: DISCRIMINATORS.LoadOne,
                },
            ),
        );

export const remove =
    (id: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/wishlists/${id}`,
                    },
                },
                "DELETE",
                false,
            ),
        );

        if (action.type === constants.SUCCESS) {
            await dispatch(loadAll(true));
        }

        return action;
    };

export const clear = actions.clear;
export const refetch = actions.refetch;
export const APIStatus = constants;
